import { useCallback, useEffect, useMemo } from 'react'

import { useConnect } from 'redux-bundler-hook'

import { ChevronRightRounded } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  List as MuiList,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'

import { Breadcrumbs, LoadingOverlay } from '@common/components'
import { parseApiErrors, useSmallScreen } from '@common/utils'
import { homeUrls } from '@portal/pages/Home'
import propertyUrls from '@portal/pages/Properties/urls'
import unitUrls from '@portal/pages/Units/urls'
import ListPageTitle from '@portal/UI/components/ListPageTitle'

import SummaryPieChart from './SummaryPieChart'

const PAGE_TITLE = 'Live Metrics'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.data
 * @param {'property'|'unit'} props.type
 */
function SummaryList({ summary, type }) {
  const isSmallScreen = useSmallScreen()

  const { doUpdateUrl } = useConnect('doUpdateUrl')

  // eslint-disable-next-line no-nested-ternary
  const PIE_SIZE = isSmallScreen.mobile ? 130 : isSmallScreen.desktop ? 250 : 300

  const sortedSummary = useMemo(
    () =>
      summary?.sort((a, b) => {
        const { id: idA, name: nameA } = a
        const { id: idB, name: nameB } = b
        if (idA === null && idB !== null) {
          return 1
        }
        if (idA !== null && idB === null) {
          return -1
        }
        return nameA.localeCompare(nameB)
      }),
    [summary],
  )

  const onItemClick = (groupId) => {
    let url
    let status
    let propertyStatus
    switch (type) {
      case 'property':
        url = propertyUrls.list
        status = 'LIVE'
        break
      case 'unit':
        url = unitUrls.list
        status = 'ONLINE,OFFLINE'
        propertyStatus = 'LIVE'
        break
      default:
        url = null
    }
    if (url) {
      const redirectUrl = `${url}?active=true&organization_group=${groupId}&status=${status}`
      doUpdateUrl(
        propertyStatus
          ? `${redirectUrl}&propertyStatus=${propertyStatus}`
          : redirectUrl,
      )
    }
  }

  if (!sortedSummary) return null

  return (
    <>
      <Divider sx={{ p: 0 }} />
      <MuiList sx={{ width: '100%', bgcolor: 'background.paper' }} component="nav">
        {sortedSummary.map((item) => {
          const { id, name, count, percentage } = item

          return (
            <Card key={`${id}_${name}`} elevation={3} sx={{ mt: 1 }}>
              <ListItemButton
                disableRipple={!id}
                onClick={id ? () => onItemClick(id) : null}
                style={{
                  backgroundColor: !id ? 'transparent' : null,
                  cursor: id ? 'pointer' : 'default',
                }}
              >
                <ListItemText primary={name} />
                <Box display="flex" alignItems="center">
                  <Typography>{`${count} (${percentage}%)`}</Typography>
                  {id && <ChevronRightRounded />}
                </Box>
              </ListItemButton>
            </Card>
          )
        })}
      </MuiList>
      <Divider sx={{ p: 0, pt: 1 }} />
      <Box mt={2}>
        <SummaryPieChart
          width={PIE_SIZE}
          height={PIE_SIZE}
          data={sortedSummary}
          onEntityClick={onItemClick}
        />
      </Box>
    </>
  )
}

export default function LiveMetrics() {
  const isSmallScreen = useSmallScreen()

  const {
    liveMetricsData,
    liveMetricsDataError,
    liveMetricsDataIsLoading,
    doFetchLiveMetricsData,
    doShowSnackbar,
  } = useConnect(
    'selectLiveMetricsData',
    'selectLiveMetricsDataError',
    'selectLiveMetricsDataIsLoading',
    'doFetchLiveMetricsData',
    'doShowSnackbar',
  )

  const fetchData = useCallback(async () => {
    try {
      await doFetchLiveMetricsData()
    } catch (err) {
      const parsedError = parseApiErrors(err?.response)
      doShowSnackbar(parsedError, 'error')
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [])

  const cardStyle = { elevation: 4, sx: { width: 1, p: 2 } }

  const cardTitleStyle = {
    variant: 'h6',
    sx: { userSelect: 'none' },
  }

  const totalStyle = {
    fontSize: 60,
    fontWeight: 600,
    color: 'success',
    textAlign: 'center',
    sx: { userSelect: 'none', mb: 2 },
  }

  return (
    <Box display="flex" flexDirection="column" m={3}>
      <Breadcrumbs
        links={[{ label: 'Home', href: homeUrls.home }, { label: PAGE_TITLE }]}
      />
      <ListPageTitle title={PAGE_TITLE} mb={2} />
      <LoadingOverlay loading={liveMetricsDataIsLoading}>
        {liveMetricsDataError && (
          <Alert
            severity="error"
            action={
              <Button size="small" color="error" onClick={fetchData}>
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            {parseApiErrors(liveMetricsDataError) || 'Oops. Something went wrong'}
          </Alert>
        )}
        <Box
          display="flex"
          flexDirection={isSmallScreen.mobile ? 'column' : 'row'}
          alignItems="start"
          justifyContent="space-between"
          gap={4}
          mb={3}
        >
          <Card {...cardStyle}>
            <Typography {...cardTitleStyle}>Total Hotels Live</Typography>
            <Typography {...totalStyle}>
              {liveMetricsData?.properties?.total ?? '--'}
            </Typography>
            <SummaryList
              summary={liveMetricsData?.properties?.summary}
              type="property"
            />
          </Card>
          <Card {...cardStyle}>
            <Typography {...cardTitleStyle}>Total Rooms Live</Typography>
            <Typography {...totalStyle}>
              {liveMetricsData?.units?.total ?? '--'}
            </Typography>
            <SummaryList summary={liveMetricsData?.units?.summary} type="unit" />
          </Card>
        </Box>
      </LoadingOverlay>
    </Box>
  )
}
